import React, { useState } from 'react';
import { useRecordContext } from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CodeIcon from '@mui/icons-material/Code';
import ReplayIcon from '@mui/icons-material/Replay';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import ViewLogsButton from './report/ViewLogsButton.component';
import useLeadImportActions from '../hooks/useLeadImportActions';

const ActionsMenu = ({ record: propRecord }) => {
  // Local state for the menu
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Get record from context if not provided as props
  const contextRecord = useRecordContext();
  const record = propRecord || contextRecord;
  
  // Use our custom hook
  const {
    // States
    showLogs,
    showRollbackDialog,
    showCancelDialog,
    isRollbackLoading,
    isCancelLoading,
    
    // Capabilities
    canViewLogs,
    canRollbackJob: canRollback,
    canRetryJob: canRetry,
    canCancelNow,
    showCancel,
    shouldShowDownload,
    isRetryLoading,

    // Actions
    handleShowDetails: onShowDetails,
    handleDownloadReport: onDownloadReport,
    handleViewLogs: onViewLogs,
    handleCloseLogs,
    handleRollback: onRollback,
    handleRollbackConfirm,
    handleRetry: onRetry,
    handleCancel: onCancel,
    handleCancelConfirm,
    
    // Dialog closers
    closeRollbackDialog,
    closeCancelDialog,
  } = useLeadImportActions(record);
  
  // Menu management
  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };
  
  // Adapted versions of handlers for the menu
  const handleShowDetails = (event) => {
    event.stopPropagation();
    handleClose();
    onShowDetails();
  };
  
  const handleDownloadReport = (event) => {
    event.stopPropagation();
    handleClose();
    onDownloadReport();
  };
  
  const handleViewLogs = (event) => {
    event.stopPropagation();
    handleClose();
    onViewLogs();
  };
  
  const handleRollback = (event) => {
    event.stopPropagation();
    handleClose();
    onRollback();
  };
  
  const handleRetry = (event) => {
    event.stopPropagation();
    handleClose();
    onRetry();
  };

  const handleCancel = (event) => {
    event.stopPropagation();
    handleClose();
    onCancel();
  };

  if (!record) return null;

  return (
    <>
      <IconButton
        aria-label="more actions"
        aria-controls="actions-menu"
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        PaperProps={{
          sx: {
            minWidth: 160,
            boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
          },
          onClick: (event) => event.stopPropagation(),
        }}
      >
        <MenuItem onClick={handleShowDetails}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {shouldShowDownload && (
          <MenuItem onClick={handleDownloadReport}>
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Report</ListItemText>
          </MenuItem>
        )}
        {canViewLogs && (
          <MenuItem onClick={handleViewLogs}>
            <ListItemIcon>
              <CodeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Processing Logs</ListItemText>
          </MenuItem>
        )}
        {showCancel && (
          <MenuItem onClick={handleCancel} >
            <ListItemIcon>
              <CancelIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>
              Cancel Job
              {/* {!canCancelNow && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Available in {15 - Math.floor((Date.now() - record.createdAt * 1000) / (1000 * 60))} min
                </Typography>
              )} */}
            </ListItemText>
          </MenuItem>
        )}
        {canRollback && (
          <MenuItem onClick={handleRollback}>
            <ListItemIcon>
              <UndoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rollback</ListItemText>
          </MenuItem>
        )}
        {canRetry && (
          <MenuItem onClick={handleRetry} disabled={isRetryLoading}>
            <ListItemIcon>
              <ReplayIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>
              {isRetryLoading ? 'Retrying...' : 'Retry'}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {showLogs && (
        <ViewLogsButton
          record={record}
          openImmediately={true}
          onClose={handleCloseLogs}
        />
      )}

      {/* Confirmation dialog for cancel */}
      <Dialog
        open={showCancelDialog}
        onClose={closeCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Cancel Import Job
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You are about to cancel the import job for "{record?.filename}".
            </Typography>
          </Alert>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              This action will:
            </Typography>
            <ul>
              <li>Stop any processing that hasn't started yet</li>
              <li>Set the job status to CANCELLED</li>
              <li>Keep the uploaded file in storage for audit purposes</li>
              <li>Allow you to upload the file again if needed</li>
            </ul>
          </Box>

          <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
            No data will be processed or sent to Braze.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeCancelDialog}
            disabled={isCancelLoading}
          >
            Keep Job
          </Button>
          <Button
            onClick={handleCancelConfirm}
            disabled={isCancelLoading}
            color="warning"
            variant="contained"
            startIcon={isCancelLoading ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {isCancelLoading ? 'Cancelling...' : 'Cancel Job'}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Confirmation dialog for rollback */}
      <Dialog
        open={showRollbackDialog}
        onClose={closeRollbackDialog}
        aria-labelledby="rollback-dialog-title"
      >
        <DialogTitle id="rollback-dialog-title">
          Confirm Import Rollback
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to rollback this import? This action will:
            <ul>
              <li>Delete all active leads from this import batch</li>
              <li>Mark the import as rolled back</li>
              <li>Update external systems to invalidate these leads</li>
            </ul>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={closeRollbackDialog} 
            disabled={isRollbackLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRollbackConfirm}
            color="error"
            variant="contained"
            disabled={isRollbackLoading}
            startIcon={isRollbackLoading ? <CircularProgress size={16} /> : <UndoIcon />}
          >
            {isRollbackLoading ? 'Rolling Back...' : 'Confirm Rollback'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActionsMenu;
